/**
 * Demo mode — axios adapter that intercepts /api/* calls and serves mock data
 * with localStorage persistence.
 *
 * Activate via env: `VITE_DEMO_MODE=1`. When active, any credential pair is
 * accepted at login and a synthetic JWT is issued so the UI can be iterated
 * without a running backend.
 */

import type {
  AxiosAdapter,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import { AxiosHeaders } from "axios";

import type {
  Access,
  ActiveConnection,
  Client,
  DashboardSummary,
  LoginResponse,
  Mountpoint,
  User,
} from "@/types/api";

const DEMO_STORE_KEY = "caster-manager.demo.state";

// ============================================================
// State
// ============================================================
interface DemoState {
  users: User[];
  mountpoints: Mountpoint[];
  clients: Client[];
  access: Access[]; // (client_id, mountpoint_id, is_enabled)
  connections: ActiveConnection[];
  // map for passwords (NOT secure — demo only)
  userPasswords: Record<string, string>;
}

function nowIso(): string {
  return new Date().toISOString();
}

function uid(prefix = ""): string {
  return (
    prefix +
    Math.random().toString(36).slice(2, 10) +
    Date.now().toString(36).slice(-4)
  );
}

function seed(): DemoState {
  const adminId = uid("u-");
  const opId = uid("u-");
  const sysId = uid("u-");
  const created = "2026-04-01T12:00:00Z";

  const users: User[] = [
    {
      id: adminId,
      email: "admin@amelia.am",
      name: "Caster Admin",
      is_enabled: true,
      last_login_at: nowIso(),
      created_at: created,
      updated_at: created,
    },
    {
      id: opId,
      email: "laura@amelia.am",
      name: "Laura Méndez",
      is_enabled: true,
      last_login_at: "2026-05-19T08:42:00Z",
      created_at: created,
      updated_at: created,
    },
    {
      id: sysId,
      email: "ops@amelia.am",
      name: "Servicio Caster",
      is_enabled: false,
      last_login_at: null,
      created_at: created,
      updated_at: created,
    },
  ];

  const mountpoints: Mountpoint[] = [
    {
      id: uid("mp-"),
      name: "AMELIA00",
      description: "Base principal — Bogotá Norte",
      format: "RTCM3.3",
      is_enabled: true,
      source_username: "base-amelia-01",
      source_password: null,
      created_at: "2026-01-12T10:00:00Z",
      updated_at: created,
    },
    {
      id: uid("mp-"),
      name: "ZOLO01",
      description: "Base secundaria — Zona Industrial Sur",
      format: "RTCM3.2",
      is_enabled: true,
      source_username: "base-zolo-02",
      source_password: null,
      created_at: "2026-02-05T10:00:00Z",
      updated_at: created,
    },
    {
      id: uid("mp-"),
      name: "MEDLN03",
      description: "Base regional — Medellín El Poblado",
      format: "RTCM3.3",
      is_enabled: true,
      source_username: "base-medln-03",
      source_password: null,
      created_at: "2026-03-15T10:00:00Z",
      updated_at: created,
    },
    {
      id: uid("mp-"),
      name: "CALI02",
      description: "Base regional — Cali Norte",
      format: "RTCM3.2",
      is_enabled: false,
      source_username: "base-cali-04",
      source_password: null,
      created_at: "2026-03-22T10:00:00Z",
      updated_at: created,
    },
  ];

  const clients: Client[] = [
    {
      id: uid("c-"),
      name: "GeoMedida S.A.S.",
      username: "topografia.gomez",
      password: null,
      is_enabled: true,
      last_connection_at: nowIso(),
      created_at: "2026-04-12T10:00:00Z",
      updated_at: created,
    },
    {
      id: uid("c-"),
      name: "Castro Topografía",
      username: "felipe.castro",
      password: null,
      is_enabled: true,
      last_connection_at: "2026-05-20T18:14:00Z",
      created_at: "2026-03-30T10:00:00Z",
      updated_at: created,
    },
    {
      id: uid("c-"),
      name: "Constructora Bolívar",
      username: "obras.norte",
      password: null,
      is_enabled: true,
      last_connection_at: "2026-05-20T16:30:00Z",
      created_at: "2026-04-02T10:00:00Z",
      updated_at: created,
    },
    {
      id: uid("c-"),
      name: "Aero Survey",
      username: "drones.medellin",
      password: null,
      is_enabled: true,
      last_connection_at: "2026-05-20T15:00:00Z",
      created_at: "2026-05-01T10:00:00Z",
      updated_at: created,
    },
  ];

  // Grant: cliente 0 → mp 0 ; cliente 1 → mp 0 ; cliente 2 → mp 1 ; cliente 3 → mp 2
  const access: Access[] = [
    grant(clients[0].id, mountpoints[0].id),
    grant(clients[1].id, mountpoints[0].id),
    grant(clients[2].id, mountpoints[1].id),
    grant(clients[3].id, mountpoints[2].id),
  ];

  const connections: ActiveConnection[] = [
    {
      session_id: "demo-session-1",
      role: "consumer",
      client_username: "topografia.gomez",
      mountpoint: "AMELIA00",
      connected_since: new Date(Date.now() - 1000 * 60 * 18).toISOString(),
      bytes_received: 1_540_320,
      remote_addr: "10.0.0.21:54321",
    },
    {
      session_id: "demo-session-2",
      role: "consumer",
      client_username: "felipe.castro",
      mountpoint: "AMELIA00",
      connected_since: new Date(Date.now() - 1000 * 60 * 6).toISOString(),
      bytes_received: 420_180,
      remote_addr: "10.0.0.34:55102",
    },
    {
      session_id: "demo-session-3",
      role: "consumer",
      client_username: "obras.norte",
      mountpoint: "ZOLO01",
      connected_since: new Date(Date.now() - 1000 * 60 * 47).toISOString(),
      bytes_received: 3_220_120,
      remote_addr: "10.0.0.45:55310",
    },
  ];

  return {
    users,
    mountpoints,
    clients,
    access,
    connections,
    userPasswords: {
      [adminId]: "demo",
      [opId]: "demo",
      [sysId]: "demo",
    },
  };

  function grant(client_id: string, mountpoint_id: string): Access {
    return {
      id: uid("a-"),
      client_id,
      mountpoint_id,
      is_enabled: true,
      revoked_at: null,
      created_at: created,
      updated_at: created,
    };
  }
}

function load(): DemoState {
  try {
    const raw = localStorage.getItem(DEMO_STORE_KEY);
    if (raw) return JSON.parse(raw) as DemoState;
  } catch {
    /* fall through */
  }
  const fresh = seed();
  save(fresh);
  return fresh;
}

function save(state: DemoState) {
  localStorage.setItem(DEMO_STORE_KEY, JSON.stringify(state));
}

let state: DemoState | null = null;

function db(): DemoState {
  if (!state) state = load();
  return state;
}

function persist() {
  if (state) save(state);
}

// ============================================================
// Route matching
// ============================================================
type Method = "GET" | "POST" | "PATCH" | "DELETE";

interface RouteCtx {
  params: Record<string, string>;
  body: unknown;
  config: InternalAxiosRequestConfig;
}

type Handler = (ctx: RouteCtx) =>
  | { status?: number; data: unknown }
  | Promise<{ status?: number; data: unknown }>;

interface Route {
  method: Method;
  pattern: RegExp;
  keys: string[];
  handler: Handler;
}

function compile(method: Method, path: string, handler: Handler): Route {
  const keys: string[] = [];
  const pattern = new RegExp(
    "^" +
      path
        .replace(/[.+^${}()|[\]\\]/g, "\\$&")
        .replace(/:([A-Za-z0-9_]+)/g, (_m, key) => {
          keys.push(key);
          return "([^/]+)";
        }) +
      "/?$",
  );
  return { method, pattern, keys, handler };
}

function jsonBody(config: InternalAxiosRequestConfig): unknown {
  const raw = config.data;
  if (raw == null) return undefined;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

// ============================================================
// Domain helpers
// ============================================================
function ok<T>(data: T, status = 200): { status: number; data: T } {
  return { status, data };
}

function notFound(): { status: number; data: { error: string; code: string } } {
  return { status: 404, data: { error: "Not found", code: "not_found" } };
}

function badRequest(msg: string): {
  status: number;
  data: { error: string; code: string };
} {
  return { status: 400, data: { error: msg, code: "bad_request" } };
}

// ============================================================
// Routes
// ============================================================
const routes: Route[] = [
  // ---- Auth ----
  compile("POST", "/api/auth/login", ({ body }) => {
    const { email, password } = (body ?? {}) as { email?: string; password?: string };
    if (!email || !password) return badRequest("email/password requeridos");
    let user = db().users.find((u) => u.email.toLowerCase() === email.toLowerCase());
    if (!user) {
      user = {
        id: uid("u-"),
        email,
        name: email.split("@")[0],
        is_enabled: true,
        last_login_at: nowIso(),
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      db().users.unshift(user);
      db().userPasswords[user.id] = password;
    } else {
      user.last_login_at = nowIso();
      user.updated_at = nowIso();
    }
    persist();
    const payload: LoginResponse = {
      access_token: `demo.${uid("tk-")}`,
      expires_at: new Date(Date.now() + 24 * 3600 * 1000).toISOString(),
      user,
    };
    return ok(payload);
  }),

  compile("POST", "/api/auth/logout", () => ok({ ok: true }, 204)),

  compile("GET", "/api/auth/me", ({ config }) => {
    const auth = config.headers?.get?.("Authorization");
    if (!auth) return { status: 401, data: { error: "no token", code: "unauthorized" } };
    const u = db().users[0];
    if (!u) return notFound();
    return ok(u);
  }),

  // ---- Dashboard ----
  compile("GET", "/api/dashboard/summary", () => {
    const s: DashboardSummary = {
      mountpoints_total: db().mountpoints.length,
      mountpoints_enabled: db().mountpoints.filter((m) => m.is_enabled).length,
      clients_total: db().clients.length,
      clients_enabled: db().clients.filter((c) => c.is_enabled).length,
      sessions_active: db().connections.length,
    };
    return ok(s);
  }),

  compile("GET", "/api/connections/active", () => ok(db().connections)),

  compile("POST", "/api/config/apply", () =>
    ok({
      applied: true,
      regenerated_files: [
        "/etc/ntripcaster/sourcetable.dat",
        "/etc/ntripcaster/clientpasswd",
        "/etc/ntripcaster/sourcepasswd",
      ],
    }),
  ),

  // ---- Mountpoints ----
  compile("GET", "/api/mountpoints", () => ok(db().mountpoints)),

  compile("GET", "/api/mountpoints/:id", ({ params }) => {
    const mp = db().mountpoints.find((m) => m.id === params.id);
    return mp ? ok(mp) : notFound();
  }),

  compile("POST", "/api/mountpoints", ({ body }) => {
    const { name, format, description, source_username, source_password } =
      (body ?? {}) as {
        name?: string;
        format?: string;
        description?: string;
        source_username?: string;
        source_password?: string;
      };
    if (!name || !format) return badRequest("name/format requeridos");
    if (!source_username || !source_password)
      return badRequest("source_username/source_password requeridos");
    const created: Mountpoint = {
      id: uid("mp-"),
      name,
      description: description ?? null,
      format,
      is_enabled: true,
      source_username,
      source_password,
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    db().mountpoints.push(created);
    persist();
    return ok(created, 201);
  }),

  compile("PATCH", "/api/mountpoints/:id", ({ params, body }) => {
    const mp = db().mountpoints.find((m) => m.id === params.id);
    if (!mp) return notFound();
    Object.assign(mp, body ?? {});
    mp.updated_at = nowIso();
    persist();
    return ok(mp);
  }),

  compile("POST", "/api/mountpoints/:id/enable", ({ params }) =>
    toggleMountpoint(params.id, true),
  ),
  compile("POST", "/api/mountpoints/:id/disable", ({ params }) =>
    toggleMountpoint(params.id, false),
  ),

  compile("DELETE", "/api/mountpoints/:id", ({ params }) => {
    const state = db();
    const idx = state.mountpoints.findIndex((m) => m.id === params.id);
    if (idx === -1) return notFound();
    state.mountpoints.splice(idx, 1);
    state.access = state.access.filter((a) => a.mountpoint_id !== params.id);
    persist();
    return ok(null as unknown as object, 204);
  }),

  compile("GET", "/api/mountpoints/:id/clients", ({ params }) => {
    const grants = db().access.filter((a) => a.mountpoint_id === params.id);
    const ids = new Set(grants.map((g) => g.client_id));
    return ok(db().clients.filter((c) => ids.has(c.id)));
  }),

  // ---- Clients ----
  compile("GET", "/api/clients", () => ok(db().clients)),

  compile("GET", "/api/clients/:id", ({ params }) => {
    const c = db().clients.find((x) => x.id === params.id);
    return c ? ok(c) : notFound();
  }),

  compile("POST", "/api/clients", ({ body }) => {
    const { name, username, password } = (body ?? {}) as {
      name?: string;
      username?: string;
      password?: string;
    };
    if (!name) return badRequest("name requerido");
    if (!username || !password)
      return badRequest("username/password requeridos");
    const created: Client = {
      id: uid("c-"),
      name,
      username,
      password,
      is_enabled: true,
      last_connection_at: null,
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    db().clients.push(created);
    persist();
    return ok(created, 201);
  }),

  compile("PATCH", "/api/clients/:id", ({ params, body }) => {
    const c = db().clients.find((x) => x.id === params.id);
    if (!c) return notFound();
    Object.assign(c, body ?? {});
    c.updated_at = nowIso();
    persist();
    return ok(c);
  }),

  compile("POST", "/api/clients/:id/enable", ({ params }) =>
    toggleClient(params.id, true),
  ),
  compile("POST", "/api/clients/:id/disable", ({ params }) =>
    toggleClient(params.id, false),
  ),

  compile("DELETE", "/api/clients/:id", ({ params }) => {
    const state = db();
    const idx = state.clients.findIndex((c) => c.id === params.id);
    if (idx === -1) return notFound();
    state.clients.splice(idx, 1);
    state.access = state.access.filter((a) => a.client_id !== params.id);
    persist();
    return ok(null as unknown as object, 204);
  }),

  compile("GET", "/api/clients/:id/mountpoints", ({ params }) => {
    const grants = db().access.filter((a) => a.client_id === params.id && a.is_enabled);
    const ids = new Set(grants.map((g) => g.mountpoint_id));
    return ok(db().mountpoints.filter((m) => ids.has(m.id)));
  }),

  // ---- Access ----
  compile("GET", "/api/access", ({ config }) => {
    const params = (config.params ?? {}) as {
      client_id?: string;
      mountpoint_id?: string;
      include_revoked?: string | boolean;
    };
    let rows = db().access;
    if (!params.include_revoked) rows = rows.filter((a) => a.is_enabled);
    if (params.client_id) rows = rows.filter((a) => a.client_id === params.client_id);
    if (params.mountpoint_id) rows = rows.filter((a) => a.mountpoint_id === params.mountpoint_id);
    return ok(rows);
  }),

  compile("POST", "/api/access", ({ body }) => {
    const { client_id, mountpoint_id } = (body ?? {}) as {
      client_id?: string;
      mountpoint_id?: string;
    };
    if (!client_id || !mountpoint_id) return badRequest("client_id/mountpoint_id requeridos");
    let row = db().access.find(
      (a) => a.client_id === client_id && a.mountpoint_id === mountpoint_id,
    );
    if (row) {
      row.is_enabled = true;
      row.revoked_at = null;
      row.updated_at = nowIso();
    } else {
      row = {
        id: uid("a-"),
        client_id,
        mountpoint_id,
        is_enabled: true,
        revoked_at: null,
        created_at: nowIso(),
        updated_at: nowIso(),
      };
      db().access.push(row);
    }
    persist();
    return ok(row, 201);
  }),

  compile("DELETE", "/api/access/:id", ({ params }) => {
    const i = db().access.findIndex((a) => a.id === params.id);
    if (i === -1) return notFound();
    db().access.splice(i, 1);
    persist();
    return ok({}, 204);
  }),

  // ---- Users ----
  compile("GET", "/api/users", () => ok(db().users)),

  compile("POST", "/api/users", ({ body }) => {
    const { email, name, password } = (body ?? {}) as {
      email?: string;
      name?: string;
      password?: string;
    };
    if (!email || !name || !password) return badRequest("email/name/password requeridos");
    const created: User = {
      id: uid("u-"),
      email,
      name,
      is_enabled: true,
      last_login_at: null,
      created_at: nowIso(),
      updated_at: nowIso(),
    };
    db().users.push(created);
    db().userPasswords[created.id] = password;
    persist();
    return ok(created, 201);
  }),

  compile("PATCH", "/api/users/:id", ({ params, body }) => {
    const u = db().users.find((x) => x.id === params.id);
    if (!u) return notFound();
    Object.assign(u, body ?? {});
    u.updated_at = nowIso();
    persist();
    return ok(u);
  }),

  compile("POST", "/api/users/:id/reset-password", ({ params, body }) => {
    const u = db().users.find((x) => x.id === params.id);
    if (!u) return notFound();
    const { new_password } = (body ?? {}) as { new_password?: string };
    if (!new_password) return badRequest("new_password requerido");
    db().userPasswords[u.id] = new_password;
    u.updated_at = nowIso();
    persist();
    return ok(u);
  }),

  // ---- Agent (Casper) — mock determinista para modo demo --------------------
  compile("POST", "/api/agent/chat", ({ body }) => {
    const { sessionId, message } = (body ?? {}) as {
      sessionId?: string;
      message?: string;
    };
    const q = (message ?? "").trim();
    const lower = q.toLowerCase();

    let reply: string;
    if (!q) {
      reply = "No recibí ningún mensaje. ¿Qué te gustaría saber?";
    } else if (/(cuántos|cuantos|count).*mountpoint/.test(lower)) {
      const total = db().mountpoints.length;
      const on = db().mountpoints.filter((m) => m.is_enabled).length;
      reply = `Hay **${on} mountpoints habilitados** de un total de ${total}.`;
    } else if (/(cuántos|cuantos).*client/.test(lower) || /list.*client/.test(lower)) {
      const names = db().clients.map((c) => `- \`${c.username}\` — ${c.name}`).join("\n");
      reply = `Hay **${db().clients.length} clientes** registrados:\n${names}`;
    } else if (/sesion|conex/.test(lower)) {
      reply = `Hay **${db().connections.length} sesiones NTRIP activas** en este momento (modo demo — datos sintéticos).`;
    } else if (/qué es|que es|explica.*mountpoint/.test(lower)) {
      reply =
        "Un **mountpoint** es un canal NTRIP donde una estación base publica correcciones RTCM. " +
        "Cada mountpoint tiene un formato (`RTCM3.2`, `RTCM3.3`), un publisher autenticado y un set de clientes con acceso vía ACL.";
    } else if (/crea|create/.test(lower)) {
      reply =
        "En modo demo no ejecuto creaciones reales. En producción te pediría confirmación explícita antes de crear " +
        "el recurso y luego mostraría las credenciales devueltas (una sola vez).";
    } else {
      reply =
        `Modo demo — eco: *"${q}"*.\n\nEn producción consultaría las tools del caster ` +
        "(`dashboard_summary`, `list_mountpoints`, etc.) y te respondería con datos reales.";
    }

    return ok({
      reply,
      sessionId: sessionId ?? "demo-session",
      actionsExecuted: [],
    });
  }),
];

function toggleMountpoint(id: string, enable: boolean) {
  const mp = db().mountpoints.find((m) => m.id === id);
  if (!mp) return notFound();
  mp.is_enabled = enable;
  mp.updated_at = nowIso();
  persist();
  return ok(mp);
}

function toggleClient(id: string, enable: boolean) {
  const c = db().clients.find((x) => x.id === id);
  if (!c) return notFound();
  c.is_enabled = enable;
  c.updated_at = nowIso();
  persist();
  return ok(c);
}

// ============================================================
// Adapter
// ============================================================
async function dispatch(config: InternalAxiosRequestConfig): Promise<AxiosResponse> {
  const method = (config.method ?? "get").toUpperCase() as Method;
  // Strip baseURL if axios resolved it absolute on us.
  let url = config.url ?? "";
  if (url.startsWith("http")) {
    try {
      url = new URL(url).pathname;
    } catch {
      /* keep as-is */
    }
  }
  // Drop query string for matching.
  const path = url.split("?")[0];

  for (const r of routes) {
    if (r.method !== method) continue;
    const m = r.pattern.exec(path);
    if (!m) continue;
    const params: Record<string, string> = {};
    r.keys.forEach((k, i) => (params[k] = decodeURIComponent(m[i + 1])));
    const body = jsonBody(config);
    const result = await r.handler({ params, body, config });
    const status = result.status ?? 200;
    return {
      data: result.data,
      status,
      statusText: status >= 200 && status < 300 ? "OK" : "Error",
      headers: new AxiosHeaders({ "content-type": "application/json" }),
      config,
      request: {},
    };
  }

  // Unknown route → 404 (still a "resolved" axios response so the dev sees it).
  const status = 404;
  const err = new Error(`[demo] Unknown route ${method} ${path}`);
  // eslint-disable-next-line no-console
  console.warn(err.message);
  return Promise.reject({
    isAxiosError: true,
    message: err.message,
    response: {
      data: { error: err.message, code: "not_found" },
      status,
      statusText: "Not Found",
      headers: {},
      config,
    },
    config,
  });
}

export const demoAdapter: AxiosAdapter = (config) => dispatch(config);

export function isDemoMode(): boolean {
  return import.meta.env.VITE_DEMO_MODE === "1";
}
